/**
 * TelehealthVideoRoom Component
 *
 * Main video call interface with Twilio Video integration
 * and side panel for documentation. Supports Voice AI transcription.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Video,
  VideoOff,
  Mic,
  MicOff,
  PhoneOff,
  FileText,
  Clock,
  User,
  AlertCircle,
  Settings,
  Maximize2,
  Minimize2,
  MessageSquare,
  Users,
  Radio,
  StopCircle,
  PlayCircle,
  Loader2,
  CheckCircle,
  XCircle,
  Wifi,
  WifiOff,
  RefreshCw,
} from 'lucide-react';
import {
  telehealthSessionAPI,
  telehealthEncounterAPI,
  telehealthVoiceAIAPI,
  telehealthConfigAPI,
  telehealthVideoAPI,
} from '@/services/api/telehealth';
import {
  TelehealthVideoRoomProps,
  TelehealthSession,
  TelehealthEncounter,
  SessionOutcome,
  EncounterDocumentation,
  CONNECTION_QUALITY_OPTIONS,
  TranscriptionResult,
  TranscriptionSegment,
} from './types';
import SessionStatusBadge from './SessionStatusBadge';
import EncounterDocumentationPanel from './EncounterDocumentationPanel';
import VoiceAIConsentModal from './VoiceAIConsentModal';
import useTwilioVideo from '@/hooks/useTwilioVideo';
import TwilioParticipant from './TwilioParticipant';

export default function TelehealthVideoRoom({
  session,
  encounter,
  onEndCall,
  onDocumentationUpdate,
  className = '',
}: TelehealthVideoRoomProps) {
  const queryClient = useQueryClient();

  // UI state
  const [showDocPanel, setShowDocPanel] = useState(true);
  const [showTranscriptPanel, setShowTranscriptPanel] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showEndCallModal, setShowEndCallModal] = useState(false);
  const [selectedOutcome, setSelectedOutcome] = useState<SessionOutcome>('completed_full');
  const [outcomeNotes, setOutcomeNotes] = useState('');

  // Video state
  const [videoToken, setVideoToken] = useState<string | null>(null);
  const [roomName, setRoomName] = useState<string | null>(null);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [isInitializingVideo, setIsInitializingVideo] = useState(true);

  // Voice AI state
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionId, setTranscriptionId] = useState<number | null>(null);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentPrompted, setConsentPrompted] = useState(false);

  // Fetch Voice AI config
  const { data: voiceAIConfig } = useQuery({
    queryKey: ['telehealth-config'],
    queryFn: () => telehealthConfigAPI.getConfig(),
  });

  // Fetch consent status
  const { data: consentStatus, refetch: refetchConsent } = useQuery({
    queryKey: ['telehealth-consent', session.id],
    queryFn: () => telehealthVoiceAIAPI.getConsentStatus(session.id),
    enabled: !!voiceAIConfig?.voice_ai_enabled,
  });

  // Record Voice-AI consent (previously only in the waiting room, which the
  // one-click Join flow now skips — so prompt for it here in the room).
  const recordConsentMutation = useMutation({
    mutationFn: (data: {
      consent_granted: boolean;
      consent_method: 'verbal' | 'written' | 'digital';
      notes?: string;
    }) => telehealthVoiceAIAPI.recordConsent(session.id, data),
    onSuccess: () => {
      setShowConsentModal(false);
      refetchConsent();
    },
  });

  // Auto-prompt for consent once, when the org enables Voice AI + auto-prompt and
  // consent hasn't been decided yet.
  useEffect(() => {
    if (
      !consentPrompted &&
      voiceAIConfig?.voice_ai_enabled &&
      voiceAIConfig?.voice_ai_auto_prompt_consent &&
      consentStatus &&
      (!consentStatus.consent_status || consentStatus.consent_status === 'not_requested')
    ) {
      setShowConsentModal(true);
      setConsentPrompted(true);
    }
  }, [consentPrompted, voiceAIConfig, consentStatus]);

  // Fetch transcription results (poll when transcribing)
  const { data: transcriptionData, refetch: refetchTranscription } = useQuery({
    queryKey: ['telehealth-transcription', session.id],
    queryFn: () => telehealthVoiceAIAPI.getTranscription(session.id),
    enabled: isTranscribing || !!transcriptionId,
    refetchInterval: isTranscribing ? 3000 : false,
  });

  // Use Twilio Video hook
  const {
    room,
    localParticipant,
    remoteParticipants,
    isConnecting,
    isConnected,
    error: twilioError,
    connect,
    disconnect,
    toggleAudio,
    toggleVideo,
    isAudioEnabled,
    isVideoEnabled,
    localVideoRef,
    networkQualityLevel,
    dominantSpeaker,
  } = useTwilioVideo({
    token: videoToken,
    roomName: roomName,
    onConnected: () => {
      console.log('Connected to Twilio video room');
      setVideoError(null);
    },
    onDisconnected: () => {
      console.log('Disconnected from Twilio video room');
    },
    onError: (error) => {
      console.error('Twilio video error:', error);
      setVideoError(error.message);
    },
    onParticipantConnected: (participant) => {
      console.log('Participant connected:', participant.identity);
    },
    onParticipantDisconnected: (participant) => {
      console.log('Participant disconnected:', participant.identity);
    },
    startMuted: false,
    startVideoOff: session.is_audio_only,
    enableNetworkQuality: true,
  });

  // Initialize Twilio video room
  useEffect(() => {
    const initializeVideo = async () => {
      setIsInitializingVideo(true);
      setVideoError(null);

      try {
        // Create video room (or get existing)
        console.log('Creating Twilio video room for session:', session.id);
        const roomResponse = await telehealthVideoAPI.createVideoRoom(session.id);
        console.log('Video room created:', roomResponse);

        // Get access token
        const tokenResponse = await telehealthVideoAPI.getVideoToken(session.id);
        console.log('Got video token for room:', tokenResponse.room_name);

        setRoomName(tokenResponse.room_name);
        setVideoToken(tokenResponse.token);
      } catch (error: any) {
        console.error('Failed to initialize Twilio video:', error);
        const errorMessage =
          error.response?.data?.detail || error.message || 'Failed to initialize video';
        setVideoError(errorMessage);
      } finally {
        setIsInitializingVideo(false);
      }
    };

    initializeVideo();
  }, [session.id]);

  // Connect to room when token is ready
  useEffect(() => {
    if (videoToken && roomName && !isConnected && !isConnecting && !twilioError) {
      console.log('Connecting to Twilio room...');
      connect();
    }
  }, [videoToken, roomName, isConnected, isConnecting, twilioError, connect]);

  // Start transcription mutation
  const startTranscriptionMutation = useMutation({
    mutationFn: () => telehealthVoiceAIAPI.startTranscription(session.id),
    onSuccess: (data) => {
      setTranscriptionId(data.transcription_id);
      setIsTranscribing(true);
      setShowTranscriptPanel(true);
    },
  });

  // Stop transcription mutation
  const stopTranscriptionMutation = useMutation({
    mutationFn: () => telehealthVoiceAIAPI.stopTranscription(session.id),
    onSuccess: () => {
      setIsTranscribing(false);
      refetchTranscription();
    },
  });

  // Apply AI suggestions mutation
  const applyAISuggestionsMutation = useMutation({
    mutationFn: (fields: string[]) =>
      telehealthVoiceAIAPI.applyTranscriptionSuggestions(session.id, fields),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['telehealth-encounter', encounter?.id] });
    },
  });

  // Check if Voice AI can be used
  const canUseVoiceAI =
    voiceAIConfig?.voice_ai_enabled && consentStatus?.consent_status === 'granted';

  // End session mutation
  const endSessionMutation = useMutation({
    mutationFn: (data: { outcome: SessionOutcome; notes?: string }) =>
      telehealthSessionAPI.endSession(session.id, {
        outcome: data.outcome,
        outcome_notes: data.notes,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['telehealth-session', session.id] });
      onEndCall(selectedOutcome, outcomeNotes);
    },
  });

  // Elapsed time timer
  useEffect(() => {
    if (session.status === 'in_progress' && session.actual_start_time) {
      const startTime = new Date(session.actual_start_time).getTime();
      const timer = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [session.status, session.actual_start_time]);

  // Format elapsed time
  const formatElapsedTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle end call
  const handleEndCall = useCallback(() => {
    // Stop transcription if running
    if (isTranscribing) {
      stopTranscriptionMutation.mutate();
    }

    // Disconnect from Twilio
    disconnect();

    // End the video room on the server
    telehealthVideoAPI.endVideoRoom(session.id).catch(console.error);

    // Show end call modal for outcome selection
    setShowEndCallModal(true);
  }, [disconnect, isTranscribing, session.id, stopTranscriptionMutation]);

  // Handle documentation update
  const handleDocUpdate = useCallback(
    (documentation: Partial<EncounterDocumentation>) => {
      onDocumentationUpdate?.(documentation);
    },
    [onDocumentationUpdate]
  );

  // Retry video connection
  const retryConnection = useCallback(async () => {
    setVideoError(null);
    setIsInitializingVideo(true);

    try {
      const tokenResponse = await telehealthVideoAPI.getVideoToken(session.id);
      setVideoToken(tokenResponse.token);
      setRoomName(tokenResponse.room_name);
    } catch (error: any) {
      setVideoError(error.response?.data?.detail || error.message);
    } finally {
      setIsInitializingVideo(false);
    }
  }, [session.id]);

  // Get network quality display
  const getNetworkQualityDisplay = (level: number | null) => {
    if (level === null) return { color: 'text-gray-400', label: 'Unknown' };
    if (level >= 4) return { color: 'text-green-500', label: 'Excellent' };
    if (level >= 3) return { color: 'text-yellow-500', label: 'Good' };
    if (level >= 2) return { color: 'text-orange-500', label: 'Fair' };
    return { color: 'text-red-500', label: 'Poor' };
  };

  const networkQuality = getNetworkQualityDisplay(networkQualityLevel);

  return (
    <div className={`flex flex-col h-screen bg-gray-900 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-teal-400" />
            <span className="text-white font-medium">
              {session.member_name || `Member #${session.member_id}`}
            </span>
          </div>
          <SessionStatusBadge status={session.status} />
          {isConnected && (
            <div className="flex items-center gap-2 px-2 py-1 bg-gray-700 rounded">
              <Wifi className={`h-3 w-3 ${networkQuality.color}`} />
              <span className={`text-xs ${networkQuality.color}`}>{networkQuality.label}</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-4">
          {/* Call timer */}
          <div className="flex items-center gap-2 text-white">
            <Clock className="h-4 w-4 text-gray-400" />
            <span className="font-mono">{formatElapsedTime(elapsedTime)}</span>
          </div>

          {/* Connection status */}
          {isConnecting && (
            <div className="flex items-center gap-2 text-yellow-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Connecting...</span>
            </div>
          )}
          {isConnected && (
            <div className="flex items-center gap-2 text-green-400">
              <CheckCircle className="h-4 w-4" />
              <span className="text-sm">Connected</span>
            </div>
          )}
          {videoError && (
            <div className="flex items-center gap-2 text-red-400">
              <XCircle className="h-4 w-4" />
              <span className="text-sm">Error</span>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Video Area */}
        <div className="flex-1 flex flex-col">
          {/* Video Grid */}
          <div className="flex-1 relative bg-gray-900 p-4">
            {/* Loading state */}
            {isInitializingVideo && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-900 z-20">
                <div className="text-center">
                  <Loader2 className="h-12 w-12 text-teal-500 animate-spin mx-auto mb-4" />
                  <p className="text-white text-lg">Initializing video...</p>
                  <p className="text-gray-400 text-sm mt-2">Setting up secure connection</p>
                </div>
              </div>
            )}

            {/* Error state */}
            {videoError && !isInitializingVideo && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-900 z-20">
                <div className="text-center max-w-md">
                  <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                  <p className="text-white text-lg mb-2">Video Connection Failed</p>
                  <p className="text-gray-400 text-sm mb-6">{videoError}</p>
                  <button
                    onClick={retryConnection}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-v2accent hover:bg-v2accent/90 text-white rounded-lg transition-colors"
                  >
                    <RefreshCw className="h-5 w-5" />
                    Retry Connection
                  </button>
                </div>
              </div>
            )}

            {/* Video participants grid */}
            {!videoError && !isInitializingVideo && (
              <div className="h-full grid grid-cols-2 gap-4">
                {/* Local participant */}
                <div className="relative aspect-video bg-gray-800 rounded-lg overflow-hidden">
                  <video
                    ref={localVideoRef}
                    autoPlay
                    muted
                    playsInline
                    className={`w-full h-full object-cover ${!isVideoEnabled ? 'hidden' : ''}`}
                  />
                  {!isVideoEnabled && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
                      <div className="w-20 h-20 rounded-full bg-teal-700 flex items-center justify-center">
                        <User className="w-10 h-10 text-white" />
                      </div>
                    </div>
                  )}
                  <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/50 rounded text-white text-sm">
                    You {!isAudioEnabled && '(Muted)'}
                  </div>
                  {!isAudioEnabled && (
                    <div className="absolute top-2 right-2 p-1 bg-red-500 rounded">
                      <MicOff className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>

                {/* Remote participants */}
                {remoteParticipants.length > 0 ? (
                  remoteParticipants.map((participant) => (
                    <TwilioParticipant
                      key={participant.sid}
                      participant={participant}
                      className="aspect-video"
                      isDominantSpeaker={dominantSpeaker?.sid === participant.sid}
                      showNetworkQuality={true}
                    />
                  ))
                ) : (
                  <div className="aspect-video bg-gray-800 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-20 h-20 rounded-full bg-purple-700 flex items-center justify-center mx-auto mb-4">
                        <User className="w-10 h-10 text-white" />
                      </div>
                      <p className="text-gray-400">Waiting for member to join...</p>
                      <p className="text-gray-500 text-sm mt-1">
                        {session.member_name || `Member #${session.member_id}`}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Control Bar */}
          <div className="flex items-center justify-center gap-4 py-4 bg-gray-800 border-t border-gray-700">
            {/* Mute toggle */}
            <button
              onClick={toggleAudio}
              disabled={!isConnected}
              className={`p-4 rounded-full transition-colors ${
                isAudioEnabled
                  ? 'bg-gray-600 hover:bg-gray-500'
                  : 'bg-red-600 hover:bg-red-500'
              } disabled:opacity-50`}
              title={isAudioEnabled ? 'Mute' : 'Unmute'}
            >
              {isAudioEnabled ? (
                <Mic className="h-6 w-6 text-white" />
              ) : (
                <MicOff className="h-6 w-6 text-white" />
              )}
            </button>

            {/* Video toggle */}
            {!session.is_audio_only && (
              <button
                onClick={toggleVideo}
                disabled={!isConnected}
                className={`p-4 rounded-full transition-colors ${
                  isVideoEnabled
                    ? 'bg-gray-600 hover:bg-gray-500'
                    : 'bg-red-600 hover:bg-red-500'
                } disabled:opacity-50`}
                title={isVideoEnabled ? 'Turn off camera' : 'Turn on camera'}
              >
                {isVideoEnabled ? (
                  <Video className="h-6 w-6 text-white" />
                ) : (
                  <VideoOff className="h-6 w-6 text-white" />
                )}
              </button>
            )}

            {/* Voice AI Recording - only show if consent granted */}
            {canUseVoiceAI && (
              <button
                onClick={() => {
                  if (isTranscribing) {
                    stopTranscriptionMutation.mutate();
                  } else {
                    startTranscriptionMutation.mutate();
                  }
                }}
                disabled={
                  startTranscriptionMutation.isPending || stopTranscriptionMutation.isPending
                }
                className={`p-4 rounded-full transition-colors ${
                  isTranscribing
                    ? 'bg-red-500 hover:bg-red-600 animate-pulse'
                    : 'bg-purple-600 hover:bg-purple-700'
                }`}
                title={isTranscribing ? 'Stop transcription' : 'Start Voice AI transcription'}
              >
                {startTranscriptionMutation.isPending || stopTranscriptionMutation.isPending ? (
                  <Loader2 className="h-6 w-6 text-white animate-spin" />
                ) : isTranscribing ? (
                  <StopCircle className="h-6 w-6 text-white" />
                ) : (
                  <Radio className="h-6 w-6 text-white" />
                )}
              </button>
            )}

            {/* End call */}
            <button
              onClick={handleEndCall}
              className="p-4 rounded-full bg-red-600 hover:bg-red-700 transition-colors"
              title="End call"
            >
              <PhoneOff className="h-6 w-6 text-white" />
            </button>

            {/* Toggle transcription panel - show if we have transcription data */}
            {(canUseVoiceAI || transcriptionData) && (
              <button
                onClick={() => setShowTranscriptPanel(!showTranscriptPanel)}
                className={`p-4 rounded-full transition-colors ${
                  showTranscriptPanel
                    ? 'bg-purple-500 hover:bg-purple-600'
                    : 'bg-gray-600 hover:bg-gray-500'
                }`}
                title={showTranscriptPanel ? 'Hide transcript' : 'Show transcript'}
              >
                <MessageSquare className="h-6 w-6 text-white" />
              </button>
            )}

            {/* Toggle documentation panel */}
            <button
              onClick={() => setShowDocPanel(!showDocPanel)}
              className={`p-4 rounded-full transition-colors ${
                showDocPanel ? 'bg-teal-500 hover:bg-teal-600' : 'bg-gray-600 hover:bg-gray-500'
              }`}
              title={showDocPanel ? 'Hide documentation' : 'Show documentation'}
            >
              <FileText className="h-6 w-6 text-white" />
            </button>

            {/* Fullscreen toggle */}
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-4 rounded-full bg-gray-600 hover:bg-gray-500 transition-colors"
              title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              {isFullscreen ? (
                <Minimize2 className="h-6 w-6 text-white" />
              ) : (
                <Maximize2 className="h-6 w-6 text-white" />
              )}
            </button>
          </div>
        </div>

        {/* Transcription Side Panel */}
        {showTranscriptPanel && (
          <div className="w-[350px] border-l border-gray-700 bg-gray-900 overflow-hidden flex-shrink-0 flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Radio className="h-5 w-5 text-purple-400" />
                <h3 className="font-semibold text-white">Voice AI Transcript</h3>
              </div>
              {isTranscribing && (
                <div className="flex items-center gap-2 text-red-400 text-sm">
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  Recording
                </div>
              )}
            </div>

            {/* Transcript Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {transcriptionData?.segments && transcriptionData.segments.length > 0 ? (
                transcriptionData.segments.map((segment: TranscriptionSegment, idx: number) => (
                  <div
                    key={idx}
                    className={`p-3 rounded-lg ${
                      segment.speaker === 'care_coordinator'
                        ? 'bg-teal-900/30 border-l-2 border-teal-500'
                        : 'bg-purple-900/30 border-l-2 border-purple-500'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span
                        className={`text-xs font-medium ${
                          segment.speaker === 'care_coordinator'
                            ? 'text-teal-400'
                            : 'text-purple-400'
                        }`}
                      >
                        {segment.speaker === 'care_coordinator' ? 'You' : 'Member'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {Math.floor(segment.start_time / 60)}:
                        {String(Math.floor(segment.start_time % 60)).padStart(2, '0')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-200">{segment.text}</p>
                  </div>
                ))
              ) : transcriptionData?.transcript_text ? (
                <div className="p-3 bg-gray-800 rounded-lg">
                  <p className="text-sm text-gray-200 whitespace-pre-wrap">
                    {transcriptionData.transcript_text}
                  </p>
                </div>
              ) : isTranscribing ? (
                <div className="text-center text-gray-400 py-8">
                  <Loader2 className="h-8 w-8 mx-auto mb-3 animate-spin text-purple-400" />
                  <p>Listening...</p>
                  <p className="text-xs mt-1">Transcript will appear here</p>
                </div>
              ) : (
                <div className="text-center text-gray-400 py-8">
                  <Radio className="h-8 w-8 mx-auto mb-3 opacity-50" />
                  <p>No transcript yet</p>
                  <p className="text-xs mt-1">Click the recording button to start</p>
                </div>
              )}
            </div>

            {/* AI Suggestions */}
            {transcriptionData?.ai_suggested_notes && (
              <div className="p-4 border-t border-gray-700 bg-gray-800/50">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-purple-300">AI Suggestions</span>
                  <button
                    onClick={() =>
                      applyAISuggestionsMutation.mutate([
                        'subjective',
                        'objective',
                        'assessment',
                        'plan',
                      ])
                    }
                    disabled={applyAISuggestionsMutation.isPending}
                    className="text-xs px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50"
                  >
                    {applyAISuggestionsMutation.isPending ? 'Applying...' : 'Apply All to Notes'}
                  </button>
                </div>
                {transcriptionData.ai_summary && (
                  <p className="text-xs text-gray-300 bg-gray-900/50 p-2 rounded">
                    {transcriptionData.ai_summary}
                  </p>
                )}
                {transcriptionData.ai_extracted_sdoh_domains &&
                  transcriptionData.ai_extracted_sdoh_domains.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {transcriptionData.ai_extracted_sdoh_domains.map((domain: string) => (
                        <span
                          key={domain}
                          className="text-xs px-2 py-0.5 bg-blue-900/30 text-blue-300 rounded"
                        >
                          {domain}
                        </span>
                      ))}
                    </div>
                  )}
              </div>
            )}
          </div>
        )}

        {/* Documentation Side Panel */}
        {showDocPanel && (
          <div className="w-[400px] border-l border-gray-700 bg-white dark:bg-gray-900 overflow-hidden flex-shrink-0">
            <EncounterDocumentationPanel
              encounter={encounter}
              isEditing={true}
              onUpdate={handleDocUpdate}
              onSave={() => {
                queryClient.invalidateQueries({ queryKey: ['telehealth-encounter'] });
              }}
              onSign={() => {
                queryClient.invalidateQueries({ queryKey: ['telehealth-encounter'] });
              }}
            />
          </div>
        )}
      </div>

      {/* End Call Modal */}
      {showEndCallModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
              End Session
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              How would you like to end this session?
            </p>

            {/* Outcome selection */}
            <div className="space-y-2 mb-4">
              {[
                { value: 'completed_full', label: 'Completed - Full session' },
                { value: 'completed_partial', label: 'Completed - Partial session' },
                { value: 'rescheduled', label: 'Rescheduled' },
                { value: 'technical_failure', label: 'Technical issues' },
              ].map((option) => (
                <label
                  key={option.value}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedOutcome === option.value
                      ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750'
                  }`}
                >
                  <input
                    type="radio"
                    name="outcome"
                    value={option.value}
                    checked={selectedOutcome === option.value}
                    onChange={(e) => setSelectedOutcome(e.target.value as SessionOutcome)}
                    className="text-teal-600"
                  />
                  <span className="text-gray-900 dark:text-gray-100">{option.label}</span>
                </label>
              ))}
            </div>

            {/* Notes */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notes (optional)
              </label>
              <textarea
                value={outcomeNotes}
                onChange={(e) => setOutcomeNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Any additional notes about the session..."
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowEndCallModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  endSessionMutation.mutate({
                    outcome: selectedOutcome,
                    notes: outcomeNotes,
                  });
                }}
                disabled={endSessionMutation.isPending}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {endSessionMutation.isPending ? 'Ending...' : 'End Session'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Voice AI consent prompt (restored in-room; previously lived in the waiting room) */}
      {showConsentModal && (
        <VoiceAIConsentModal
          open={showConsentModal}
          session={session}
          consentScript={voiceAIConfig?.consent_script_text}
          onConsent={async (granted, method, notes) => {
            await recordConsentMutation.mutateAsync({
              consent_granted: granted,
              consent_method: method,
              notes,
            });
          }}
          onClose={() => setShowConsentModal(false)}
        />
      )}
    </div>
  );
}
